import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { HCaptchaField } from "../components/HCaptchaField";
import { useCustomer } from "../customer/CustomerContext";
import { requiredMessage } from "../utils/formValidation";

export function ResetPasswordPage() {
  const { customer } = useCustomer();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = (searchParams.get("token") || "").trim();
  const [token, setToken] = useState(tokenFromUrl);
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaReset, setCaptchaReset] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (customer) return <Navigate to="/account" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!token.trim()) errs.token = "Open the reset link from your email.";
    if (!otp.trim()) errs.otp = requiredMessage("OTP");
    if (!password) errs.password = requiredMessage("Password");
    else if (password.length < 8) errs.password = "Password must be at least 8 characters.";
    if (password && password !== password2) errs.password2 = "Passwords do not match.";
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      setError("Please fill in the required fields.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/customers/reset-password", {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({
          token: token.trim(),
          otp: otp.trim(),
          password,
          h_captcha_response: captchaToken || undefined,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        field_errors?: Record<string, string>;
        message?: string;
      };
      if (!res.ok) {
        if (data.field_errors) setFieldErrors(data.field_errors);
        throw new Error(data.error || "Reset failed.");
      }
      navigate("/account/login", { replace: true, state: { resetOk: true } });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reset failed.");
      setCaptchaToken("");
      setCaptchaReset((n) => n + 1);
    } finally {
      setLoading(false);
    }
  }

  return (
    <article className="page account-page">
      <p className="eyebrow">Customer account</p>
      <h1>Reset password</h1>
      <p className="page-summary">
        Enter the one-time code from your email and choose a new password. The OTP expires
        after a few minutes.
      </p>
      {!tokenFromUrl ? (
        <p className="model-run-error" role="alert">
          Missing reset token. Use the link from your email, or{" "}
          <Link to="/account/forgot-password">request a new reset</Link>.
        </p>
      ) : null}
      <form className="deposit-form account-form" onSubmit={onSubmit} noValidate>
        {!tokenFromUrl ? (
          <div className="buy-server-row">
            <label htmlFor="reset-token">
              Reset token <span className="req-star">*</span>
            </label>
            <div className="buy-server-control">
              <input
                id="reset-token"
                type="text"
                autoComplete="off"
                value={token}
                aria-invalid={Boolean(fieldErrors.token)}
                onChange={(e) => setToken(e.target.value)}
              />
              {fieldErrors.token ? <p className="field-error">{fieldErrors.token}</p> : null}
            </div>
          </div>
        ) : (
          <input type="hidden" value={token} readOnly />
        )}
        <div className="buy-server-row">
          <label htmlFor="reset-otp">
            One-time code (OTP) <span className="req-star">*</span>
          </label>
          <div className="buy-server-control">
            <input
              id="reset-otp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              value={otp}
              aria-invalid={Boolean(fieldErrors.otp)}
              onChange={(e) => setOtp(e.target.value.replace(/\s/g, ""))}
            />
            {fieldErrors.otp ? <p className="field-error">{fieldErrors.otp}</p> : null}
          </div>
        </div>
        <div className="buy-server-row">
          <label htmlFor="reset-password">
            New password <span className="req-star">*</span>
          </label>
          <div className="buy-server-control">
            <input
              id="reset-password"
              type="password"
              autoComplete="new-password"
              value={password}
              aria-invalid={Boolean(fieldErrors.password)}
              onChange={(e) => setPassword(e.target.value)}
            />
            {fieldErrors.password ? <p className="field-error">{fieldErrors.password}</p> : null}
          </div>
        </div>
        <div className="buy-server-row">
          <label htmlFor="reset-password2">
            Confirm password <span className="req-star">*</span>
          </label>
          <div className="buy-server-control">
            <input
              id="reset-password2"
              type="password"
              autoComplete="new-password"
              value={password2}
              aria-invalid={Boolean(fieldErrors.password2)}
              onChange={(e) => setPassword2(e.target.value)}
            />
            {fieldErrors.password2 ? (
              <p className="field-error">{fieldErrors.password2}</p>
            ) : null}
          </div>
        </div>
        <HCaptchaField purpose="login" onToken={setCaptchaToken} resetKey={captchaReset} />
        {fieldErrors.captcha ? <p className="field-error">{fieldErrors.captcha}</p> : null}
        {error ? (
          <p className="model-run-error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="buy-server-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Updating…" : "Update password"}
          </button>
        </div>
      </form>
      <p className="account-switch">
        <Link to="/account/forgot-password">Request a new link</Link>
        {" · "}
        <Link to="/account/login">Log in</Link>
      </p>
    </article>
  );
}
